import { UserState } from "core/state/userState";
import { useNavigate } from "react-router-dom";
import { useLogReportingEventFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { useVideoCall } from "./useVideoCall";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { useWisdomLite } from "core/hooks/useWisdomLite";
import { useModalContext } from "shared/core/state/ModalContext/ModalContext";
import { getMediaPermissions } from "core/javascriptChannels";
import { ReportingEventType } from "shared/constants/ReportingEventType";
import { satisfies } from "semver";
import { MediaPermissions } from "../Support";
import { useRef, useState } from "react";
import { useUserContext } from "core/state/UserContext/UserContext";
import { WisdomModules } from "shared/constants/Modules";
import { WisdomLiteNotice } from "../Components/WisdomLiteNotice";
import { WisdomAIButtonCard } from "../Components/WisdomAIButtonCard";
import { ClickableRoundedCard } from "../Components/ContactCard/ClickableRoundedCard";
import { ReactComponent as VideoCallIcon } from "@icons/video-call.svg";
import { ReactComponent as VideoCallUnavailableIcon } from "@icons/video-call-unavailable.svg";
import { t } from "i18next";
import { SafeSpaces } from "../../Home/components/SafeSpaces/SafeSpaces";
import { MediaPermissionsModal } from "../Components/MicrophoneModal/MediaPermissionsModal";
import HelplineBlock from "../Components/HelplineBlock";
import parseHelplineNumbers from "../utils/helpline";
import { ReactComponent as WhatsAppIcon } from "shared/assets/whats-app.svg";
import { ReactComponent as WhatsAppUnavailableIcon } from "shared/assets/whatsapp-unavailable.svg";
import { CopyBlock } from "shared/UI/CopyBlock/CopyBlock";
import useLocale from "core/hooks/useLocale/useLocale";
import { LiveChatCard } from "../Components/LiveChat/LiveChat";
import { useWhatsApp } from "./useWhatsApp/useWhatsApp";

const useSupportData = () => {
  const user = UserState.get();
  const helplineData = user?.helplinePhoneNumber ?? "";
  const navigate = useNavigate();
  const { trigger } = useLogReportingEventFunctionRequest();
  const { isWisdomLite } = useWisdomLite();
  const { isVideoCallAvailable, startVideoCall } = useVideoCall();
  const { isMobileApp, appVersion } = useMobilePlatform();
  const { isVisible: isMediaPermissionVisible, show: showMediaPermission, hide: hideMediaPermission } = useModalContext("media-permissions");
  const [requiredPermissions, setRequiredPermissions] = useState<string[]>();
  const { isPermitted } = useUserContext();
  const { isLocalePermitted } = useLocale();
  const { startWhatsAppChat, isCardDisabled, description, useWhatsAppIntegration, ComingSoonModal, TermsModal } = useWhatsApp();

  const whatsAppBlockRef = useRef<HTMLDivElement>(null);

  const scrollToWhatsApp = () => {
    if (whatsAppBlockRef?.current) {
      whatsAppBlockRef!.current!.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const hasNewVideoVersion = appVersion && satisfies(appVersion, process.env.REACT_APP_VIDEO_CALL_VERSION!);

  const handleVideoCall = async () => {
    const allPermissions = Object.values(MediaPermissions);
    const mediaPermissions: string[] = await getMediaPermissions();

    if (!isMobileApp || !hasNewVideoVersion || (isMobileApp && mediaPermissions.length === allPermissions.length)) {
      trigger({ body: { reportingEventType: ReportingEventType.SupportRequestVideoCall } }).finally(startVideoCall);
    } else {
      const permissions = allPermissions.filter((item) => !mediaPermissions.includes(item));
      setRequiredPermissions(permissions);
      showMediaPermission();
    }
  };

  const liveChatComponent = (
    <LiveChatCard
      handleWhatsAppScroll={scrollToWhatsApp}
      startWhatsAppChat={startWhatsAppChat}
      isWhatsAppDisabled={isCardDisabled}
      useWhatsAppIntegration={useWhatsAppIntegration}
    />
  );

  const videoChatComponent = (
    <ClickableRoundedCard
      className="h-full"
      title={t("support.videoCall.title")}
      icon={isVideoCallAvailable ? <VideoCallIcon /> : <VideoCallUnavailableIcon />}
      description={isVideoCallAvailable ? t("support.videoCall.description") : t("support.videoCall.descriptionOffline")}
      handleClick={handleVideoCall}
      showChevron={isVideoCallAvailable}
      disabled={!isVideoCallAvailable}
    />
  );

  const requestCallbackComponent = (
    <ClickableRoundedCard
      className="h-full"
      title={t("support.callback.title")}
      showChevron
      handleClick={() =>
        trigger({ body: { reportingEventType: ReportingEventType.SupportRequestRequestCallback } }).finally(() => navigate("/support/callback"))
      }
    />
  );

  const whatsAppChatComponent = (
    <div ref={whatsAppBlockRef}>
      <ClickableRoundedCard
        disabled={isCardDisabled}
        className="h-full"
        title={t("support.whatsApp.title")}
        icon={isCardDisabled ? <WhatsAppUnavailableIcon /> : <WhatsAppIcon />}
        description={<CopyBlock content={description} />}
        handleClick={startWhatsAppChat}
        showChevron={!isCardDisabled}
      />
    </div>
  );

  const mediaPermissionsModal = (
    <MediaPermissionsModal isVisible={isMediaPermissionVisible} onDismiss={hideMediaPermission} requiredPermissions={requiredPermissions} />
  );

  let allComponents = [
    { id: 1, condition: isWisdomLite, content: <WisdomLiteNotice /> },
    { id: 2, condition: !isWisdomLite, content: <WisdomAIButtonCard /> },
    {
      id: 3,
      condition: isPermitted(WisdomModules.whatsapp),
      content: whatsAppChatComponent,
    },
    {
      id: 4,
      condition: isPermitted(WisdomModules.videoChat) && isLocalePermitted("videoChat"),
      content: videoChatComponent,
    },
    {
      id: 5,
      condition: isPermitted(WisdomModules.helpline) && parseHelplineNumbers(helplineData)?.length > 0,
      content: <HelplineBlock helplineData={helplineData} />,
    },
    {
      id: 6,
      condition: isLocalePermitted("safeSpaces"),
      content: <SafeSpaces />,
    },
    {
      id: 7,
      condition: isPermitted(WisdomModules.requestCallBack),
      content: requestCallbackComponent,
    },
    {
      id: 8,
      condition: isPermitted(WisdomModules.liveChat) && isLocalePermitted("liveChat"),
      content: liveChatComponent,
    },
    {
      id: 9,
      content: mediaPermissionsModal,
    },
    ...(!!ComingSoonModal
      ? [
          {
            id: 10,
            content: ComingSoonModal,
          },
        ]
      : []),
    ...(!!TermsModal
      ? [
          {
            id: 11,
            content: TermsModal,
          },
        ]
      : []),
  ];

  return allComponents;
};
export default useSupportData;
